<template>
	<div class="my-balance">
		<div class="my-balance__title _h2">{{ $t('Deposit') }}</div>
		<div class="my-balance__top">
			<div class="my-balance__input">
				<BaseInput
					class="my-balance__field"
					v-model.number="selectedAmount"
					type="number"
					:label="`${$t('Enter your amount')}:`"
					:currency="true"
				/>
				<div class="my-balance__currency">{{ currencySymbol }}</div>
			</div>
			<div class="my-balance__top-box">
				<div class="my-balance__top-text">
					<span>{{ $t('min deposit amount is') }}:</span>
					<span>{{ minTopupAmount }} {{ currencySymbol }}</span>
				</div>
				<div class="my-balance__top-text">
					<span>{{ $t('max deposit amount is') }}:</span>
					<span>{{ maxAmount }} {{ currencySymbol }}</span>
				</div>
			</div>
		</div>
		<div class="my-balance__label">{{ $t('or choose') }}:</div>
		<div class="my-balance__actions">
			<BaseButton
				v-for="amount in amounts"
				:key="amount"
				:class="[
					'my-balance__action _btn-bordered',
					{ selected: selectedAmount === amount },
				]"
				@click="selectAmount(amount)"
				:theme="ButtonThemes.SMALL_GRADIENT"
			>
				<span>{{ amount }}</span> <span>{{ currencySymbol }}</span>
			</BaseButton>
		</div>
		<div v-if="amountError" class="my-balance__error">
			{{ amountError }}
		</div>
		<BaseButton
			@click="openDepositModal"
			class="my-balance__submit _btn-primary"
			:disabled="!isValidAmount"
		>
			{{ $t('Pay with card') }}
		</BaseButton>
	</div>
	<teleport to="#app">
		<DepositModal
			v-if="depositModalIsVisible && user"
			:user="user"
			:deposit-amount="selectedAmount"
			:payment-type="'card'"
		/>
	</teleport>
</template>

<script>
import BaseButton, { ButtonThemes } from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import DepositModal from '@/components/DepositModal.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
	components: { DepositModal, BaseButton, BaseInput },
	name: 'MyBalance',
	props: {
		user: Object,
		minTopupAmount: {
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			ButtonThemes,
			amounts: [5, 10, 25, 35, 40, 50, 100, 150, 250, 500],
			selectedAmount: 0,
			cardNumber: '',
			expiryDate: '',
			cvv: '',
			amountError: '',
			maxAmount: 1000,
		};
	},
	watch: {
		selectedAmount: {
			immediate: true,
			handler(value) {
				this.validateAmount(value);
			},
		},
		minTopupAmount() {
			this.validateAmount(this.selectedAmount);
		},
	},
	computed: {
		...mapGetters('app', ['currencySymbol', 'currencyValue']),
		...mapGetters('modals', ['depositModalIsVisible']),
		isValidAmount() {
			const amount = parseFloat(this.selectedAmount);
			const min = this.minTopupAmount;

			if (Number.isNaN(amount)) return false;
			if (amount < min || amount > this.maxAmount) return false;

			return true;
		},
	},
	methods: {
		...mapActions('modals', ['openDepositModal']),
		selectAmount(amount) {
			this.selectedAmount = amount;
		},
		openWithdraw() {
			this.$emit('openWithdraw');
		},
		validateAmount(value) {
			const amount = parseFloat(value);
			const min = this.minTopupAmount;
			const max = 1000;
			const cur = this.currencySymbol;

			if (Number.isNaN(amount)) {
				this.amountError = this.$t('Amount must be a number');
				return false;
			}

			if (amount < min || amount > max) {
				this.amountError =
					this.$t('Amount must be in range of') +
					` ${min}${cur} ` +
					this.$t('and') +
					` ${max}${cur}`;
				return false;
			}

			this.amountError = '';
			return true;
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/fonts' as *;
@use '@/assets/styles/components/classes' as *;

.my-balance {
	&__title {
		display: block;
		text-align: center;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 40, 20);
		}
	}
	&__top {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 30, 15);
		}
		@media (min-width: $md3) {
			display: flex;
			justify-content: center;
			gap: 20px;
		}
		&-box {
			align-self: flex-end;
		}
		&-text {
			display: flex;
			align-items: center;
			gap: 4px;
			&:not(:last-child) {
				margin-bottom: 8px;
			}
			span {
				font-weight: 500;
				font-size: 16px;
				color: var(--secondary-color);
				line-height: 120%;
				display: inline-block;
			}
		}
	}
	&__input {
		width: 100%;
		max-width: 450px;
		position: relative;
		@media (max-width: $md3) {
			&:not(:last-child) {
				margin-bottom: 10px;
			}
		}
	}
	&__field {
		input {
			padding-right: 45px;
		}
	}
	&__currency {
		position: absolute;
		right: 15px;
		bottom: 13px;
	}
	&__label {
		display: block;
		text-align: center;
		font-weight: 500;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 30, 15);
		}
	}
	&__actions {
		display: flex;
		justify-content: center;
		gap: 20px;
		flex-wrap: wrap;
		max-width: 800px;
		margin-left: auto;
		margin-right: auto;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 40, 20);
		}
		@media (max-width: $md5) {
			gap: 6px;
		}
	}
	&__action {
		min-width: 126px;
		pointer-events: auto;
		@media (max-width: $md5) {
			flex: 0 1 calc(50% - 3px);
		}
		&.selected {
			pointer-events: none;
			background-color: var(--hint-color);
			border: 1px solid transparent;
		}
	}
	&__error {
		font-family: var(--font-family-inter);
		font-size: 16px;
		line-height: 120%;
		font-weight: 500;
		display: block;
		text-align: center;
		margin-bottom: 8px;
		color: var(--error-color);
	}
	&__submit {
		min-height: 40px;
		@media (min-width: $md5) {
			max-width: 224px;
			margin: 0 auto;
		}
		@media (max-width: $md5) {
			width: 100%;
		}
	}
}
</style>
